import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (!res.ok) throw new Error("unauth");
      setUser(await res.json());
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth, let AuthCallback establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const login = () => {
    const redirectUrl = window.location.origin + "/mon-compte";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch {}
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh: checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Handles {redirect}#session_id=... — exchanges session_id, then routes by role.
export const AuthCallback = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    (async () => {
      const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const sessionId = params.get("session_id");
      const path = window.location.pathname;
      if (!sessionId) {
        navigate(path, { replace: true });
        return;
      }
      try {
        const res = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: { "X-Session-ID": sessionId },
          credentials: "include",
        });
        if (!res.ok) throw new Error("session failed");
        const u = await res.json();
        setUser(u);
        window.history.replaceState(null, "", path);
        navigate(u.role === "admin" ? "/admin" : "/mon-compte", { replace: true });
      } catch {
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center text-neutral-400 font-mono-accent text-xs uppercase tracking-[0.3em]">
      Connexion...
    </div>
  );
};

export const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/", { replace: true });
    else if (adminOnly && user.role !== "admin") navigate("/mon-compte", { replace: true });
  }, [user, loading, adminOnly, navigate]);

  if (loading || !user || (adminOnly && user.role !== "admin")) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-neutral-500 font-mono-accent text-xs uppercase tracking-[0.3em]">
        Chargement...
      </div>
    );
  }
  return children;
};
